import React, { useState, useEffect, useMemo } from 'react';
import { usePermify } from '@permify/react-role';
// React and hooks
import { useHistory } from 'react-router-dom';
import {
  Button,
  Col,
  Collapse,
  Row,
  Space,
  Typography,
  Card,
  Form,
  Select,
  message,
  Menu,
  Dropdown,
  Divider,
  Input,
  Spin,
  Alert,
  Tooltip,
  Table,
  Tag,
  Progress,
  Switch,
  Modal,
} from 'antd';
import {
  LoadingOutlined,
  ClockCircleOutlined,
  RocketOutlined,
  InfoCircleOutlined,
  SaveOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';

// Shared components
import { useCampaignRequestorContext } from '../../../../core/components/campaignRequestor/useCampaignRequestorContext';
import { validateCampaignOrder, validateCampaignForLaunch, validateAllDatesForLaunch } from '../../CampaignRequestor/CampaignRequestorUtil';
import { detectFlightOverlaps } from '../../CampaignRequestor/utils/campaignValidation';
import { useMutation } from 'react-apollo';
import UPDATE_CAMPAIGN_STATUS from '../../../../core/GraphQl/Mutations/UPDATE_CAMPAIGN_STATUS';
import useCampaignLaunchProgress from '../../../../core/hooks/useCampaignLaunchProgress';

// Local components
import { CampaignOrderQueueReviewCampaign } from './CampaignOrderQueueReviewCampaign';
import CampaignLaunchProgressModal from './CampaignLaunchProgressModal';

// Styles
import '../../CampaignRequestor/styles.css';
import './styles.css';
import { getCampaignOrderErrorMessage } from '../../../../core/utils/constants/campaignOrderErrorType';
import { useCampaignOrderQueueContext } from '../../../../core/components/campaigns/campaignordersqueue/useCampaignOrderQueueContext';
import moment from 'moment';
import { formatCampaignName } from '../../../../core/utils/campaignNaming';
import { formatCurrency2SigFig } from '../../../../core/utils/campaigns';
import { isPercentageModel } from '../../../../core/utils/pricing/cpmHelper';

const { Panel } = Collapse;
const { Title, Text } = Typography;

export const CampaignOrderQueueCampaignReview = ({ form, fieldName, campaignIndex, onMediaStatusUpdate, refetchCampaignOrder }) => {
  const { updateCampaignOrder, currentCampaignOrder, loadingUpdateCampaignOrder, selectedAdvertiser } =
    useCampaignRequestorContext();
  const { launchCampaignOrderProgrammatic, loadingLaunchCampaingOrderProgrammatic } =
    useCampaignOrderQueueContext();
  const [updateCampaignStatus, { loading: loadingUpdateCampaignStatus }] = useMutation(UPDATE_CAMPAIGN_STATUS);

  const campaign = form.getFieldValue('campaigns')[campaignIndex];
  const currentCampaign = currentCampaignOrder.campaigns[campaignIndex];
  const campaignId = currentCampaign?.id;
  const campaignStatus = currentCampaign?.status;

  // Check if any OTHER campaign in this campaign order is currently launching PROGRAMMATICALLY
  // Manual launches can happen in parallel, only programmatic launches need to be sequential
  const getOtherProgrammaticLaunchingCampaign = () => {
    return currentCampaignOrder.campaigns.find((camp, index) => {
      if (index === campaignIndex) return false; // Skip current campaign
      const isLaunchingProgrammatically =
        ['LAUNCHING_LIVE', 'LAUNCHING_PAUSED'].includes(camp.status) &&
        camp.isLaunchProgrammatically === true;
      return isLaunchingProgrammatically;
    });
  };

  const otherProgrammaticLaunchingCampaign = getOtherProgrammaticLaunchingCampaign();
  const isAnotherProgrammaticCampaignLaunching = !!otherProgrammaticLaunchingCampaign;

  // Check if Ad Provider ID is pre-populated (which prevents programmatic launch)
  const hasAdProviderId = !!(campaign?.adProviderId && campaign.adProviderId.trim() !== '');

  // Detect UPDATE mode: campaign already launched with adProviderId
  const isUpdateMode = hasAdProviderId;

  // Count new line items (marked for delivery but not yet launched to DSP)
  const newLineItems = useMemo(() => {
    if (!currentCampaign?.lineItems) return [];
    return currentCampaign.lineItems.filter(
      (lineItem) => lineItem.markedForDelivery && !lineItem.dspLineItemId
    );
  }, [currentCampaign.lineItems]);

  const newLineItemCount = newLineItems.length;

  // Count modified line items (those with PENDING status that need updating)
  const modifiedLineItems = useMemo(() => {
    if (!currentCampaign?.lineItems) return [];
    return currentCampaign.lineItems.filter(
      (lineItem) => lineItem.dspLineItemId && (lineItem.status === 'PLAY_PENDING' || lineItem.status === 'PAUSED_PENDING')
    );
  }, [currentCampaign.lineItems]);

  const modifiedLineItemCount = modifiedLineItems.length;

  // Smart blocking: only block when hasAdProviderId AND no new items AND no modified items
  const hasNewItemsToLaunch = !hasAdProviderId || newLineItemCount > 0 || modifiedLineItemCount > 0;

  // Detect UPDATE mode for programmatic campaigns:
  // Campaign already launched programmatically AND has status LIVE/PAUSED/PENDING AND has new items OR modified items
  const isProgrammaticUpdateMode =
    currentCampaign?.isLaunchProgrammatically === true &&
    ['LIVE', 'LIVE_PENDING', 'LIVE_APPROVED', 'PAUSED', 'PAUSED_PENDING', 'PAUSED_APPROVED'].includes(campaignStatus) &&
    (newLineItemCount > 0 || modifiedLineItemCount > 0);

  // Check if campaign is already launched (LIVE or PAUSED including PENDING states) for button label changes
  const isCampaignLaunched = ['LIVE', 'LIVE_PENDING', 'LIVE_APPROVED', 'PAUSED', 'PAUSED_PENDING', 'PAUSED_APPROVED'].includes(campaignStatus);

  // Track campaign launch progress using campaign ID
  const isLaunching = ['LAUNCHING_LIVE', 'LAUNCHING_PAUSED'].includes(campaignStatus);

  const progressData = useCampaignLaunchProgress(campaignId, {
    enabled: isLaunching,
    autoCleanup: false,
    onCompleted: async (progress) => {
      if (refetchCampaignOrder) {
        await refetchCampaignOrder();
      }
    },
    onFailed: async (progress) => {
      if (refetchCampaignOrder) {
        await refetchCampaignOrder();
      }
    }
  });

  const { isAuthorized } = usePermify();
  const [isAdvancedMode, setIsAdvancedMode] = useState(false);
  const [demandSidePlatform, setDemandSidePlatform] = useState(
    campaign ? campaign.demandSidePlatform : null
  );
  const [isManualLaunch, setIsManualLaunch] = useState(false);
  const [dateValidationErrors, setDateValidationErrors] = useState([]);
  const [showAddLineItemsModal, setShowAddLineItemsModal] = useState(false);
  const [pendingLaunchStatus, setPendingLaunchStatus] = useState(null);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [currentLaunchStatus, setCurrentLaunchStatus] = useState(null);
  const [showOnlyDeliveryItems, setShowOnlyDeliveryItems] = useState(false);
  const [isUpdatingAdProviderId, setIsUpdatingAdProviderId] = useState(false);

  const history = useHistory();

  // Advanced Mode Detection Logic
  useEffect(() => {
    const checkAdvancedMode = async () => {
      // Check if user has required permissions (isTrafficker OR isProcessor)
      const hasPermission = await isAuthorized(['isTrafficker', 'isProcessor'], []);

      // Check if campaign status is NOT DRAFT
      const campaignStatus = currentCampaignOrder?.status;
      const isNotDraft = campaignStatus && campaignStatus !== 'DRAFT';

      // Check if at least one campaign has hasMediaPlanStrategy enabled
      // const campaigns = currentCampaignOrder?.campaigns || [];
      // const hasStrategy = campaigns.some(camp => camp.hasMediaPlanStrategy === true);

      // Advanced Mode is available when ALL conditions are met
      const canAccessAdvancedMode = hasPermission && isNotDraft;
      setIsAdvancedMode(canAccessAdvancedMode);
    };

    checkAdvancedMode();
  }, [isAuthorized, currentCampaignOrder]);

  // Determine pricing model for display
  const pricingModel = useMemo(() => {
    // Priority 1: Use pricingModel directly from campaignOrder (string value)
    let model = currentCampaignOrder?.pricingModel;

    // Priority 2: Try selectedAdvertiser.pricingModel.model
    if (!model && selectedAdvertiser?.pricingModel?.model) {
      model = selectedAdvertiser.pricingModel.model;
    }

    // Priority 3: Try currentCampaignOrder.advertiser.pricingModel.model
    if (!model && currentCampaignOrder?.advertiser?.pricingModel?.model) {
      model = currentCampaignOrder.advertiser.pricingModel.model;
    }

    if (!model) {
      return null;
    }

    if (model === 'Percentage') {
      return {
        name: 'Percentage',
        description: 'Budget-based pricing',
        color: 'purple',
        tooltip: 'This campaign uses percentage-based pricing where costs are calculated as a percentage of the budget allocation.'
      };
    }

    if (model === 'CPM') {
      return {
        name: 'CPM',
        description: 'Impression-based pricing',
        color: 'blue',
        tooltip: 'This campaign uses CPM (Cost Per Mille) pricing where costs are based on impressions delivered.'
      };
    }

    return null;
  }, [selectedAdvertiser, currentCampaignOrder]);

  // Calculate pricing value for display
  const pricingValue = useMemo(() => {
    if (!pricingModel) return null;

    const campaign = currentCampaignOrder?.campaigns?.[campaignIndex];
    if (!campaign) return null;

    if (pricingModel.name === 'CPM') {
      // Get CPM value from campaign
      const cpmValue = campaign.cpm;
      return cpmValue ? formatCurrency2SigFig(cpmValue) : null;
    }

    if (pricingModel.name === 'Percentage') {
      // Get percentage value from campaign
      const percentageValue = campaign.advertiserPercentage;
      return percentageValue ? `${percentageValue}%` : null;
    }

    return null;
  }, [pricingModel, currentCampaignOrder, campaignIndex]);

  const save = async (status, options = {}) => {
    const { updateCampaignStatus = false, isManualLaunch = false, skipValidation = false } = options;

    // Pre-flight campaign order validation (skip if already validated in caller)
    if (!skipValidation) {
      if (isManualLaunch) {
        // For manual launch, only validate the specific campaign
        if (!validateCampaignForLaunch(currentCampaignOrder, campaignIndex, message)) {
          return;
        }
      } else {
        // For other operations, validate entire campaign order
        if (!validateCampaignOrder(currentCampaignOrder, message)) {
          return;
        }
      }
    } else {
    }

    // Validate form fields
    let values;
    try {
      if (skipValidation && isManualLaunch) {
        // For manual launch with skipValidation, only validate the specific campaign's fields
        const fieldsToValidate = [
          ['campaigns', campaignIndex, 'demandSidePlatform'],
          ['campaigns', campaignIndex, 'adProviderId'],
          ['campaigns', campaignIndex, 'maxBid'],
        ];
        await form.validateFields(fieldsToValidate);

        // Get all form values (we need the whole campaigns array for the mutation)
        values = form.getFieldsValue();
      } else {
        // For other operations, validate all fields
        values = await form.validateFields();
      }
    } catch (error) {
      message.error(error.errorFields[0].errors[0]);
      return;
    }

    try {


      // For manual launch, only include the campaign being launched in the mutation
      const campaignsToUpdate = (skipValidation && isManualLaunch)
        ? [values.campaigns[campaignIndex]]  // Only the clicked campaign
        : values.campaigns;  // All campaigns for other operations


      await updateCampaignOrder({
        variables: {
          where: { id: currentCampaignOrder.id },
          data: {
            status,
            campaigns: {
              update: campaignsToUpdate.map((campaign, index) => ({
                where: { id: campaign.id },
                data: {
                  demandSidePlatform: campaign.demandSidePlatform,
                  adProviderId: campaign.adProviderId || null,
                  maxBid: Number(campaign.maxBid || 0),

                  lineItems: {
                    update: campaign.lineItems.map((lineItem) => ({
                    where: { id: lineItem.id },
                    data: {
                      supplySidePlatform: lineItem.supplySidePlatform,
                      maxBid: Number(lineItem.maxBid || 0),
                      frequencyCap: Number(lineItem.frequencyCap || 0),
                      beacons: {
                        create: (lineItem.beacons || [])
                          .filter((beacon, index, array) =>
                            array.findIndex(b => b.beaconId === beacon.beaconId) === index
                          )
                          .map((beacon) => ({
                            beaconId: beacon.beaconId,
                            name: beacon.name,
                            advertiserId: beacon.advertiserId,
                            beaconType: beacon.beaconType,
                          })),
                      },
                      dealIds: {
                        create: lineItem.dealIds.map((data) => ({
                          dealId: data.dealId,
                          name: data.name,
                          org: {
                            connect: {
                              id: currentCampaignOrder.advertiser.id,
                            },
                          },
                        })),
                      },
                    },
                  })),
                },
                },
              })),
            },
          },
        },
      });
    } catch (error) {
      message.destroy();
      message.error(getCampaignOrderErrorMessage(error));
    }
  };

  const handleLaunchManual = async () => {

    setIsManualLaunch(true);

    // Pre-flight validation for this specific campaign only
    const validationResult = validateCampaignForLaunch(currentCampaignOrder, campaignIndex, message);

    if (!validationResult) {
      setIsManualLaunch(false);
      return;
    }

    try {
      // Only validate fields for this specific campaign
      const fieldsToValidate = [
        ['campaigns', campaignIndex, 'demandSidePlatform'],
        ['campaigns', campaignIndex, 'adProviderId'],
        ['campaigns', campaignIndex, 'maxBid'],
      ];

      // Add validation for DSP Line Item IDs for line items marked for delivery
      if (isUpdateMode && currentCampaign?.lineItems) {
        currentCampaign.lineItems.forEach((lineItem, lineItemIndex) => {
          if (lineItem.markedForDelivery && !lineItem.dspLineItemId) {
            fieldsToValidate.push(['campaigns', campaignIndex, 'lineItems', lineItemIndex, 'dspLineItemId']);
          }
        });
      }

      await form.validateFields(fieldsToValidate);
    } catch (error) {
      setIsManualLaunch(false);

      // Check if there are missing DSP Line Item IDs
      const hasMissingDspIds = isUpdateMode && currentCampaign?.lineItems.some(
        (lineItem) => lineItem.markedForDelivery && !lineItem.dspLineItemId
      );

      if (hasMissingDspIds) {
        message.warning('Please enter DSP Line Item IDs for all line items marked for delivery.');
      } else {
        message.warning('Please fix the validation errors before launching.');
      }
      return;
    }

    // Validate dates for manual launch (only end dates >= start dates)
    const dateValidation = validateAllDatesForLaunch(currentCampaignOrder, campaignIndex, 'manual');
    if (!dateValidation.isValid) {
      setDateValidationErrors(dateValidation.errors);
      setIsManualLaunch(false);
      message.error('Cannot launch: One or more end dates are before start dates. Please fix dates in Campaign Requester.');
      return;
    }

    try {
      message.loading('Launching campaign...', 0);

      // Get form values for the clicked campaign
      const values = form.getFieldsValue();
      const campaignData = values.campaigns[campaignIndex];
      const campaignId = currentCampaignOrder.campaigns[campaignIndex].id;

      // Preserve programmatic launch history: only set to false if not already programmatic
      const wasLaunchedProgrammatically = currentCampaign?.isLaunchProgrammatically === true;

      // Preserve current status if updating, otherwise set to LIVE for new launches
      const currentStatus = currentCampaign?.status;
      const targetStatus = isCampaignLaunched ? currentStatus : 'LIVE';

      // Step 1: Update the campaign status
      await updateCampaignStatus({
        variables: {
          where: { id: campaignId },
          data: {
            status: targetStatus,
            demandSidePlatform: campaignData.demandSidePlatform,
            adProviderId: campaignData.adProviderId || null,
            maxBid: Number(campaignData.maxBid || 0),
            isLaunchProgrammatically: wasLaunchedProgrammatically ? true : false,
            lineItems: {
              update: campaignData.lineItems.map((lineItem) => ({
                where: { id: lineItem.id },
                data: {
                  supplySidePlatform: lineItem.supplySidePlatform,
                  maxBid: Number(lineItem.maxBid || 0),
                  frequencyCap: Number(lineItem.frequencyCap || 0),
                  dspLineItemId: lineItem.dspLineItemId || null,
                  beacons: {
                    create: (lineItem.beacons || [])
                      .filter((beacon, index, array) =>
                        array.findIndex(b => b.beaconId === beacon.beaconId) === index
                      )
                      .map((beacon) => ({
                        beaconId: beacon.beaconId,
                        name: beacon.name,
                        advertiserId: beacon.advertiserId,
                        beaconType: beacon.beaconType,
                      })),
                  },
                  dealIds: {
                    create: lineItem.dealIds.map((data) => ({
                      dealId: data.dealId,
                      name: data.name,
                      org: {
                        connect: {
                          id: currentCampaignOrder.advertiser.id,
                        },
                      },
                    })),
                  },
                },
              }))
            }
          },
        },
      });

      // Step 2: Update the campaign order status
      if (!isCampaignLaunched) {
        // New launch: Set to LIVE
        await updateCampaignOrder({
          variables: {
            where: { id: currentCampaignOrder.id },
            data: {
              status: 'LIVE',
            },
          },
        });
      } else if (currentCampaignOrder.status === 'LIVE_APPROVED' || currentCampaignOrder.status === 'PAUSED_APPROVED') {
        // Update: Transition LIVE_APPROVED → LIVE or PAUSED_APPROVED → PAUSED
        const newStatus = currentCampaignOrder.status === 'LIVE_APPROVED' ? 'LIVE' : 'PAUSED';
        await updateCampaignOrder({
          variables: {
            where: { id: currentCampaignOrder.id },
            data: {
              status: newStatus,
            },
          },
        });
      }

      message.destroy();
      message.success('Campaign launched successfully');

      // Step 3: Refresh campaign order data to show updated status
      if (refetchCampaignOrder) {
        await refetchCampaignOrder();
      }

    } catch (error) {
      message.destroy();
      message.error(getCampaignOrderErrorMessage(error));
    } finally {
      setIsManualLaunch(false);
    }
  };

  const validateProgrammaticLaunch = async (options = {}) => {
    const { skipDateValidation = false } = options;
    const validationResult = validateCampaignForLaunch(currentCampaignOrder, campaignIndex, message);

    if (!validationResult) {
      return {
        isValid: false,
        shouldShowDateModal: false,
        validationError: 'Pre-flight validation failed'
      };
    }

    try {

      const campaignFieldsToValidate = [
        ['campaigns', campaignIndex, 'demandSidePlatform'],
        ['campaigns', campaignIndex, 'adProviderId'],
        ['campaigns', campaignIndex, 'maxBid'],
      ];

      const campaign = form.getFieldValue('campaigns')[campaignIndex];
      const lineItemFieldsToValidate = [];
      if (campaign && campaign.lineItems) {
        // Only validate line items that are marked for delivery
        campaign.lineItems.forEach((lineItem, lineItemIndex) => {
          if (lineItem.markedForDelivery) {
            lineItemFieldsToValidate.push(
              ['campaigns', campaignIndex, 'lineItems', lineItemIndex, 'supplySidePlatform'],
              ['campaigns', campaignIndex, 'lineItems', lineItemIndex, 'maxBid'],
              ['campaigns', campaignIndex, 'lineItems', lineItemIndex, 'frequencyCap']
            );
          }
        });
      }

      const allFieldsToValidate = [...campaignFieldsToValidate, ...lineItemFieldsToValidate];

      await form.validateFields(allFieldsToValidate);
    } catch (error) {
      message.warning('Please fix the validation errors before launching.');
      return {
        isValid: false,
        shouldShowDateModal: false,
        validationError: 'Form validation failed'
      };
    }

    // Validate dates for programmatic launch and update
    if (!skipDateValidation) {
      const dateValidationResult = validateAllDatesForLaunch(
        currentCampaignOrder,
        campaignIndex,
        'programmatic'
      );

      if (!dateValidationResult.isValid) {
        // Block launch and return errors for display
        return {
          isValid: false,
          shouldShowDateModal: false,
          validationError: 'Date validation failed',
          dateErrors: dateValidationResult.errors
        };
      }
    }

    // Check for flight schedule overlaps
    const flightSchedules = currentCampaign?.flightSchedules || [];
    if (flightSchedules.length > 1) {
      const overlaps = detectFlightOverlaps(flightSchedules);
      if (overlaps.length > 0) {
        const overlapErrors = overlaps.map(overlap => ({
          level: 'CAMPAIGN',
          entityName: `Campaign "${currentCampaign.name || `Campaign ${campaignIndex + 1}`}"`,
          message: `Flight ${overlap.flightIndex1 + 1} and Flight ${overlap.flightIndex2 + 1} have overlapping date ranges`
        }));
        return {
          isValid: false,
          shouldShowDateModal: false,
          validationError: 'Flight schedule overlap detected',
          dateErrors: overlapErrors
        };
      }
    }

    return {
      isValid: true,
      shouldShowDateModal: false,
      validationError: null,
      dateErrors: []
    };
  };

  /**
   * Handle confirmation of adding new line items
   */
  const handleConfirmAddLineItems = async () => {
    setShowAddLineItemsModal(false);

    if (!pendingLaunchStatus) {
      message.error('No launch status specified');
      return;
    }

    // Proceed with validation
    const validation = await validateProgrammaticLaunch();

    if (!validation.isValid) {
      // If there are date errors, set state to display them
      if (validation.dateErrors && validation.dateErrors.length > 0) {
        setDateValidationErrors(validation.dateErrors);
      }
      return;
    }

    // Continue with the programmatic launch process
    await executeProgrammaticLaunch(pendingLaunchStatus);
  };

  /**
   * Handle programmatic update for campaigns already launched
   * Determines launch status based on current campaign status
   */
  const handleUpdateProgrammatic = async () => {
    // Determine launch status based on current campaign status
    const currentStatus = currentCampaign?.status;
    const launchStatus = currentStatus === 'LIVE' ? 'play' : 'pause';

    // Call the programmatic launch handler with the determined status
    await handleLaunchProgrammatic(launchStatus);
  };

  /**
   * Execute the programmatic launch after all validations
   * @param {string} launchStatus - 'pause' or 'play'
   */
  const executeProgrammaticLaunch = async (launchStatus) => {
    try {
      // Store the launch status for completion handling
      setCurrentLaunchStatus(launchStatus);

      // Get form values for the clicked campaign
      const values = form.getFieldsValue();
      const campaignData = values.campaigns[campaignIndex];
      const campaignId = currentCampaignOrder.campaigns[campaignIndex].id;


      // Determine if this is UPDATE mode (campaign already has adProviderId)
      const isUpdateMode = !!(currentCampaign?.adProviderId && currentCampaign.adProviderId.trim() !== '');

      // Step 1: Update the campaign status to APPROVED (will be updated to LIVE/PAUSED after external service completes)
      await updateCampaignStatus({
        variables: {
          where: { id: campaignId },
          data: {
            status: launchStatus === 'play' ? 'LAUNCHING_LIVE' : 'LAUNCHING_PAUSED',
            demandSidePlatform: campaignData.demandSidePlatform,
            // Only set adProviderId to null for INITIAL launch, preserve it for UPDATE mode
            adProviderId: isUpdateMode ? currentCampaign.adProviderId : null,
            maxBid: Number(campaignData.maxBid || 0),
            isLaunchProgrammatically: true,  // Programmatic launch
            lineItems: {
              update: campaignData.lineItems.map((lineItem) => ({
                where: { id: lineItem.id },
                data: {
                  supplySidePlatform: lineItem.supplySidePlatform,
                  maxBid: Number(lineItem.maxBid || 0),
                  frequencyCap: Number(lineItem.frequencyCap || 0),
                  beacons: {
                    create: (lineItem.beacons || [])
                      .filter((beacon, index, array) =>
                        array.findIndex(b => b.beaconId === beacon.beaconId) === index
                      )
                      .map((beacon) => ({
                        beaconId: beacon.beaconId,
                        name: beacon.name,
                        advertiserId: beacon.advertiserId,
                        beaconType: beacon.beaconType,
                      })),
                  },
                  dealIds: {
                    create: lineItem.dealIds.map((data) => ({
                      dealId: data.dealId,
                      name: data.name,
                      org: {
                        connect: {
                          id: currentCampaignOrder.advertiser.id,
                        },
                      },
                    })),
                  },
                },
              }))
            }
          },
        },
      });

      // Status update will be handled on the backend
      // await save(currentCampaignOrder.status);

      await launchCampaignOrderProgrammatic({
        variables: {
          data: {
            id: currentCampaignOrder.id,
            launchStatus, // pass "play" or "pause"
            alternativeName: formatCampaignName({
              ...currentCampaignOrder,
              name: currentCampaignOrder.campaigns[campaignIndex].name,
            }),
            campaignId,
          },
        },
      });

      // Update campaign order status for APPROVED variants
      if (currentCampaignOrder.status === 'LIVE_APPROVED' || currentCampaignOrder.status === 'PAUSED_APPROVED') {
        const newStatus = currentCampaignOrder.status === 'LIVE_APPROVED' ? 'LIVE' : 'PAUSED';
        await updateCampaignOrder({
          variables: {
            where: { id: currentCampaignOrder.id },
            data: {
              status: newStatus,
            },
          },
        });
      }

      if (isUpdateMode) {
        message.success(
          `Adding ${newLineItemCount} new line item${newLineItemCount !== 1 ? 's' : ''} with status: ${launchStatus === 'play' ? 'LIVE' : 'PAUSED'}`
        );
      } else {
        message.success(
          `Campaign launch initiated with status: ${launchStatus === 'play' ? 'LIVE' : 'PAUSED'}`
        );
      }

      // Small delay to ensure mutation has propagated through database
      await new Promise(resolve => setTimeout(resolve, 500));

      if (refetchCampaignOrder) {
        await refetchCampaignOrder();
      } else {
        console.warn('[CampaignReview] refetchCampaignOrder is not available!');
      }
    } catch (error) {
      console.error('❌ Error during programmatic launch:', error);
      message.error(getCampaignOrderErrorMessage(error));
    }
  };

  /**
   * Launch campaign programmatically
   * @param {string} launchStatus - 'pause' or 'play'
   */
  const handleLaunchProgrammatic = async (launchStatus = 'pause') => {
    setIsManualLaunch(false);

    // Check if Ad Provider ID is already provided - only block if no new items AND no modified items
    const campaignData = form.getFieldValue('campaigns')[campaignIndex];
    if (campaignData?.adProviderId && campaignData.adProviderId.trim() !== '') {

      // Has adProviderId - check if there are new items OR modified items
      if (newLineItemCount === 0 && modifiedLineItemCount === 0) {
        message.warning(
          'No changes to apply. All line items have already been launched and none have been modified.'
        );
        return;
      }
      // Show modal to confirm which line items will be added/updated
      setPendingLaunchStatus(launchStatus);
      setShowAddLineItemsModal(true);
      return;
    }

    const validation = await validateProgrammaticLaunch();

    if (!validation.isValid) {
      // If there are date errors, set state to display them
      if (validation.dateErrors && validation.dateErrors.length > 0) {
        setDateValidationErrors(validation.dateErrors);
      }
      return;
    }

    // Validation passed, execute launch
    await executeProgrammaticLaunch(launchStatus);
  };

  const handleUpdateAdProviderId = () => {
    const currentAdProviderId = currentCampaignOrder.campaigns[campaignIndex]?.adProviderId || '';
    const newAdProviderId = form.getFieldValue(['campaigns', campaignIndex, 'adProviderId']) || '';

    if (currentAdProviderId === newAdProviderId) {
      message.info('No changes to save. The Ad Provider ID value is unchanged.');
      return;
    }

    if (!newAdProviderId || newAdProviderId.trim() === '') {
      message.warning('Ad Provider ID cannot be empty. Use the delete button to remove it.');
      return;
    }

    Modal.confirm({
      title: 'Update Ad Provider ID',
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p>Are you sure you want to update the Ad Provider ID?</p>
          <p><strong>Current value:</strong> {currentAdProviderId || '(empty)'}</p>
          <p><strong>New value:</strong> {newAdProviderId}</p>
          <p style={{ color: '#faad14', marginTop: 12 }}>
            This change will take effect immediately.
          </p>
        </div>
      ),
      okText: 'Update',
      cancelText: 'Cancel',
      onOk: async () => {
        setIsUpdatingAdProviderId(true);
        try {
          await updateCampaignStatus({
            variables: {
              where: { id: campaignId },
              data: {
                adProviderId: newAdProviderId.trim(),
              },
            },
          });
          message.success('Ad Provider ID updated successfully');
          if (refetchCampaignOrder) {
            await refetchCampaignOrder();
          }
        } catch (error) {
          message.error(getCampaignOrderErrorMessage(error));
        } finally {
          setIsUpdatingAdProviderId(false);
        }
      },
    });
  };

  const handleRemoveAdProviderId = () => {
    const currentAdProviderId = currentCampaignOrder.campaigns[campaignIndex]?.adProviderId || '';

    if (!currentAdProviderId || currentAdProviderId.trim() === '') {
      message.info('Ad Provider ID is already empty.');
      return;
    }

    Modal.confirm({
      title: 'Remove Ad Provider ID',
      icon: <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />,
      content: (
        <div>
          <p>Are you sure you want to remove the Ad Provider ID?</p>
          <p><strong>Current value:</strong> {currentAdProviderId}</p>
          <p style={{ color: '#ff4d4f', marginTop: 12 }}>
            Warning: Removing the Ad Provider ID may impact tracking and reporting for this campaign.
          </p>
        </div>
      ),
      okText: 'Remove',
      okButtonProps: { danger: true },
      cancelText: 'Cancel',
      onOk: async () => {
        setIsUpdatingAdProviderId(true);
        try {
          await updateCampaignStatus({
            variables: {
              where: { id: campaignId },
              data: {
                adProviderId: null,
              },
            },
          });
          form.setFieldsValue({
            campaigns: form
              .getFieldValue('campaigns')
              .map((c, idx) =>
                idx === campaignIndex
                  ? { ...c, adProviderId: '' }
                  : c
              ),
          });
          message.success('Ad Provider ID removed successfully');
          if (refetchCampaignOrder) {
            await refetchCampaignOrder();
          }
        } catch (error) {
          message.error(getCampaignOrderErrorMessage(error));
        } finally {
          setIsUpdatingAdProviderId(false);
        }
      },
    });
  };

  return (
    <Spin spinning={loadingLaunchCampaingOrderProgrammatic || loadingUpdateCampaignOrder || loadingUpdateCampaignStatus || isUpdatingAdProviderId}>
      <div id={`campaign-${campaignIndex}`}>
        <Collapse bordered={false} defaultActiveKey={[0]}>
          <Panel
            open={true}
            header={
              <Space>
                <Title level={5} style={{ margin: 0 }}>Campaign</Title>
                {/* Pricing Model Indicator */}
                {pricingModel && (
                  <Tooltip title={pricingModel.tooltip}>
                    <Tag color={pricingModel.color}>
                      {pricingModel.name}: {pricingModel.description}
                    </Tag>
                  </Tooltip>
                )}
              </Space>
            }
          >
          <Card>
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              {/* Show alert when another programmatic campaign is launching */}
              {isAnotherProgrammaticCampaignLaunching &&
               !isLaunching && (
                <Alert
                  message="Sequential Launch in Progress"
                  description={
                    <span>
                      Campaign "<strong>{otherProgrammaticLaunchingCampaign?.name || 'Another campaign'}</strong>" is currently launching programmatically.
                      Please wait for it to complete before launching this campaign programmatically. Manual launches are still available.
                    </span>
                  }
                  type="info"
                  showIcon
                  icon={<LoadingOutlined />}
                  closable={false}
                />
              )}
              {/* DSP Fields - Only visible in Advanced Mode */}
              {isAdvancedMode && ['APPROVED', 'LIVE_APPROVED', 'PAUSED_APPROVED', 'LIVE', 'PAUSED'].includes(currentCampaignOrder.status) && (
                <>
                  <Row gutter={16}>
                    <Col span={6}>
                      <Space direction="vertical" size={2} style={{ width: '100%' }}>
                        <Text>DSP</Text>
                        <Text type="secondary">
                          <small> Demand-Side Platform </small>
                        </Text>
                        <Form.Item
                          name={[fieldName, 'demandSidePlatform']}
                          rules={[
                            {
                              required: true,
                              message: 'Please select a DSP',
                            },
                          ]}
                        >
                          <Select
                            style={{ width: '100%' }}
                            showSearch
                            optionFilterProp="children"
                            onChange={(value) => setDemandSidePlatform(value)}
                            filterOption={(input, option) =>
                              option.value.toLowerCase().indexOf(input.toLowerCase()) >= 0
                            }
                            options={[
                              { value: 'NEXXEN', label: 'NEXXEN' },
                              { value: 'LIQUIDM', label: 'LIQUIDM' },
                              { value: 'JAMLOOP', label: 'JAMLOOP' },
                            ]}
                            disabled={isLaunching}
                          />
                        </Form.Item>
                      </Space>
                    </Col>
                    <Col span={6}>
                      <Space direction="vertical" size={2} style={{ width: '100%' }}>
                        <Space size={8} align="center">
                          <Text>Ad Provider ID</Text>
                          <Tooltip title="Save Ad Provider ID">
                            <Button
                              type="text"
                              size="small"
                              icon={<SaveOutlined />}
                              onClick={handleUpdateAdProviderId}
                              disabled={isLaunching || isUpdatingAdProviderId}
                              style={{ padding: '0 4px' }}
                            />
                          </Tooltip>
                          <Tooltip title="Remove Ad Provider ID">
                            <Button
                              type="text"
                              size="small"
                              danger
                              icon={<DeleteOutlined />}
                              onClick={handleRemoveAdProviderId}
                              disabled={isLaunching || isUpdatingAdProviderId || !currentCampaignOrder.campaigns[campaignIndex]?.adProviderId}
                              style={{ padding: '0 4px' }}
                            />
                          </Tooltip>
                        </Space>
                        <Text type="secondary">
                          <small> Unique identifier for the ad provider </small>
                        </Text>
                        <Form.Item
                          name={[fieldName, 'adProviderId']}
                          rules={[
                            {
                              validator: (_, value) => {
                                if (isManualLaunch && (!value || value.trim() === '')) {
                                  return Promise.reject(
                                    'Ad Provider ID is required for manual launch'
                                  );
                                }
                                return Promise.resolve();
                              },
                            },
                          ]}
                        >
                          <Input
                            style={{ width: '100%' }}
                            onChange={(e) =>
                              form.setFieldsValue({
                                campaigns: form
                                  .getFieldValue('campaigns')
                                  .map((c, idx) =>
                                    idx === campaignIndex
                                      ? { ...c, adProviderId: e.target.value }
                                      : c
                                  ),
                              })
                            }
                            value={(campaign && campaign.adProviderId) || ''}
                            disabled={isLaunching || isUpdatingAdProviderId}
                          />
                        </Form.Item>
                      </Space>
                    </Col>
                    {/* Only show Maximum Bid if pricing model is not Percentage */}
                    {pricingModel?.name !== 'Percentage' && (
                      <Col span={7}>
                        <Space direction="vertical" size={2} style={{ width: '100%' }}>
                          <Text>Maximum Bid</Text>
                          <Text type="secondary">
                            <small> Maximum bid amount for the campaign </small>
                          </Text>
                          <Form.Item
                            name={[fieldName, 'maxBid']}
                            rules={[
                              {
                                required: true,
                                message: 'Maximum bid is required',
                              },
                              {
                                type: 'number',
                                min: 0.01,
                                message: 'Maximum bid must be greater than zero',
                              },
                            ]}
                          >
                            <Input
                              type="number"
                              step="0.01"
                              style={{ width: '100%' }}
                              onChange={(e) => {
                                const value = e.target.value;
                                const numericValue = value === '' ? null : parseFloat(value);
                                form.setFieldsValue({
                                  campaigns: form
                                    .getFieldValue('campaigns')
                                    .map((c, idx) =>
                                      idx === campaignIndex
                                        ? { ...c, maxBid: numericValue }
                                        : c
                                    ),
                                });
                              }}
                              value={(campaign && campaign.maxBid) || 4.00}
                              disabled={isLaunching}
                            />
                          </Form.Item>
                        </Space>
                      </Col>
                    )}
                  </Row>
                </>
              )}

              {/* Campaign Details Section */}
              <Row gutter={20}>
                <Col span={6}>
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <Text type="secondary">Campaign Type</Text>
                    <Text>
                      {(() => {
                        const typeLabels = {
                          // Numeric types
                          1: 'Display',
                          7: 'External',
                          8: 'Video',
                          // String types
                          'DISPLAY': 'Display',
                          'EXTERNAL': 'External',
                          'VIDEO': 'Video',
                        };
                        const campaignType = currentCampaignOrder.campaigns[campaignIndex]?.type;
                        return typeLabels[campaignType] || 'Display';
                      })()}
                    </Text>
                  </Space>
                </Col>
                <Col span={6}>
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <Text type="secondary">Campaign Name</Text>
                    <Text>
                      {currentCampaignOrder.campaigns[campaignIndex]?.name || `Campaign ${campaignIndex + 1}`}
                    </Text>
                  </Space>
                </Col>
                <Col span={6}>
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <Text type="secondary">Budget</Text>
                    <Text>
                      {formatCurrency2SigFig(currentCampaignOrder.campaigns[campaignIndex]?.budgetTotal || 0)}
                    </Text>
                  </Space>
                </Col>
                <Col span={6}>
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <Text type="secondary">Date Range</Text>
                    <Text>
                      {currentCampaignOrder.campaigns[campaignIndex]?.startDate &&
                       currentCampaignOrder.campaigns[campaignIndex]?.endDate
                        ? `${moment(currentCampaignOrder.campaigns[campaignIndex].startDate).format('MM/DD/YYYY')} - ${moment(currentCampaignOrder.campaigns[campaignIndex].endDate).format('MM/DD/YYYY')}`
                        : 'Not specified'}
                    </Text>
                  </Space>
                </Col>
              </Row>
              <Row gutter={20} style={{ marginTop: 16 }}>
                <Col span={6}>
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <Text type="secondary">Status</Text>
                    {(() => {
                      const status = currentCampaignOrder.campaigns[campaignIndex]?.status;
                      const statusConfig = {
                        PENDING: { color: 'default', text: 'Pending' },
                        APPROVED: { color: 'blue', text: 'Approved' },
                        LAUNCHING: { color: 'processing', text: 'Launching' },
                        LIVE: { color: 'success', text: 'Live' },
                        PAUSED: { color: 'warning', text: 'Paused' },
                        COMPLETED: { color: 'default', text: 'Completed' },
                        ARCHIVED: { color: 'default', text: 'Archived' },
                        ERROR: { color: 'error', text: 'Error' },
                      };
                      const config = statusConfig[status] || { color: 'default', text: status || 'Not Set' };
                      return <Tag color={config.color}>{config.text}</Tag>;
                    })()}
                  </Space>
                </Col>
                <Col span={6}>
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <Text type="secondary">Launch Type</Text>
                    {(() => {
                      const status = currentCampaignOrder.campaigns[campaignIndex]?.status;
                      const isLaunchProgrammatically = currentCampaignOrder.campaigns[campaignIndex]?.isLaunchProgrammatically;

                      if (!['LIVE', 'PAUSED'].includes(status)) {
                        return <Text type="secondary">-</Text>;
                      }

                      if (isLaunchProgrammatically) {
                        return <Tag color="purple">Programmatic</Tag>;
                      }
                      return <Tag color="blue">Manual</Tag>;
                    })()}
                  </Space>
                </Col>
                <Col span={6}>
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <Text type="secondary">Pricing Model</Text>
                    {pricingModel ? (
                      <Space size={4}>
                        <Tag color={pricingModel.color}>{pricingModel.name}</Tag>
                        {pricingValue && <Text strong>{pricingValue}</Text>}
                      </Space>
                    ) : (
                      <Text type="secondary">-</Text>
                    )}
                  </Space>
                </Col>
              </Row>

              {/* Campaign-Level Flight Schedules (read-only) */}
              {(() => {
                const flightSchedules = currentCampaignOrder.campaigns[campaignIndex]?.flightSchedules || [];
                if (flightSchedules.length === 0) return null;

                const isPercentage = pricingModel?.name === 'Percentage';
                const dataSource = flightSchedules.map((flight, idx) => {
                  const numberOfDays = flight.startDate && flight.endDate
                    ? moment(flight.endDate).diff(moment(flight.startDate), 'days') + 1
                    : null;
                  const impressionGoal = flight.budget && flight.cpm
                    ? Math.round((flight.budget / flight.cpm) * 1000)
                    : null;
                  return { key: idx, ...flight, numberOfDays, impressionGoal };
                });

                const columns = [
                  {
                    title: 'Flight',
                    key: 'flight',
                    width: 60,
                    align: 'center',
                    render: (_, __, idx) => <Text>{idx + 1}</Text>,
                  },
                  {
                    title: 'Start Date',
                    dataIndex: 'startDate',
                    key: 'startDate',
                    width: 120,
                    render: (value) => <Text>{value ? moment.utc(value).format('MM/DD/YYYY') : '-'}</Text>,
                  },
                  {
                    title: 'End Date',
                    dataIndex: 'endDate',
                    key: 'endDate',
                    width: 120,
                    render: (value) => <Text>{value ? moment.utc(value).format('MM/DD/YYYY') : '-'}</Text>,
                  },
                  {
                    title: 'Budget',
                    dataIndex: 'budget',
                    key: 'budget',
                    width: 120,
                    align: 'right',
                    render: (value) => <Text>{value ? formatCurrency2SigFig(value) : '-'}</Text>,
                  },
                  ...(!isPercentage ? [{
                    title: 'CPM',
                    dataIndex: 'cpm',
                    key: 'cpm',
                    width: 100,
                    align: 'right',
                    render: (value) => <Text>{value ? formatCurrency2SigFig(value) : '-'}</Text>,
                  }] : []),
                  ...(!isPercentage ? [{
                    title: 'Impressions',
                    dataIndex: 'impressionGoal',
                    key: 'impressionGoal',
                    width: 120,
                    align: 'right',
                    render: (value) => <Text>{value ? value.toLocaleString() : '-'}</Text>,
                  }] : []),
                  {
                    title: 'Days',
                    dataIndex: 'numberOfDays',
                    key: 'numberOfDays',
                    width: 70,
                    align: 'center',
                    render: (value) => <Text>{value !== null ? value : '-'}</Text>,
                  },
                ];

                return (
                  <Row gutter={20} style={{ marginTop: 16 }}>
                    <Col span={24}>
                      <Text type="secondary" style={{ marginBottom: 8, display: 'block' }}>Flight Schedules</Text>
                      <Table
                        dataSource={dataSource}
                        columns={columns}
                        pagination={false}
                        size="small"
                        bordered
                      />
                    </Col>
                  </Row>
                );
              })()}

               {/* Show Delivery Items Only Toggle - Advanced Mode Only */}
               {isAdvancedMode && (
                <Row gutter={16} style={{ marginTop: 16 }}>
                  <Col span={24}>
                    <Space align="center">
                      <Switch
                        checked={showOnlyDeliveryItems}
                        onChange={setShowOnlyDeliveryItems}
                      />
                      <Text strong>Show Delivery Items Only</Text>
                      <Tooltip title="Filter to display only line items marked for delivery">
                        <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
                      </Tooltip>
                    </Space>
                  </Col>
                </Row>
              )}

              {/* Campaign Launch Progress Section - Only show for programmatic launches */}
              {isLaunching && currentCampaignOrder.campaigns[campaignIndex]?.isLaunchProgrammatically && (
                <>
                  <Divider plain style={{ margin: '16px 0' }} />
                  <Alert
                    message={
                      <Space>
                        <RocketOutlined style={{ fontSize: 16 }} />
                        <Text strong>
                          {isUpdateMode
                            ? `Adding ${newLineItemCount} Line Item${newLineItemCount !== 1 ? 's' : ''} in Progress`
                            : 'Campaign Launch in Progress'}
                        </Text>
                      </Space>
                    }
                    description={
                      <Space direction="vertical" size="middle" style={{ width: '100%', marginTop: 12 }}>
                        {/* Progress Bar */}
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                            <Text strong style={{ marginRight: 8 }}>Progress:</Text>
                            <Text type="secondary">{progressData.progress || 0}%</Text>
                          </div>
                          <Progress
                            percent={progressData.progress || 0}
                            size="default"
                            status={
                              progressData.isFailed
                                ? 'exception'
                                : progressData.isCompleted
                                ? 'success'
                                : 'active'
                            }
                            strokeWidth={10}
                          />
                        </div>

                        {/* Current Phase and Message */}
                        <div>
                          <Space direction="vertical" size="small" style={{ width: '100%' }}>
                            {progressData.phase && (
                              <div>
                                <Text strong>Phase: </Text>
                                <Tag color="blue">{progressData.phase}</Tag>
                              </div>
                            )}
                            <div>
                              <Text strong>Status: </Text>
                              <Text type="secondary">
                                {progressData.message ||
                                  (isUpdateMode ? 'Initializing line item addition...' : 'Initializing campaign launch...')}
                              </Text>
                            </div>
                          </Space>
                        </div>

                        {/* Elapsed Time */}
                        {progressData.elapsedTimeFormatted && (
                          <div>
                            <Space>
                              <ClockCircleOutlined style={{ color: '#1890ff' }} />
                              <Text strong>Elapsed Time: </Text>
                              <Text style={{ color: '#1890ff' }}>
                                {progressData.elapsedTimeFormatted}
                              </Text>
                            </Space>
                          </div>
                        )}

                        {/* Error Details */}
                        {progressData.isFailed && progressData.progressData?.metadata?.errorDetails && (
                          <Alert
                            message="Launch Failed"
                            description={progressData.progressData.metadata.errorDetails}
                            type="error"
                            showIcon
                            style={{ marginTop: 8 }}
                          />
                        )}
                      </Space>
                    }
                    type={
                      progressData.isFailed
                        ? 'error'
                        : progressData.isCompleted
                        ? 'success'
                        : 'info'
                    }
                    showIcon
                    icon={
                      progressData.isFailed ? null : progressData.isCompleted ? null : <LoadingOutlined />
                    }
                  />
                </>
              )}

              <Divider plain></Divider>

              <Form.List name={[fieldName, 'lineItems']}>
                {(fields) => {
                  // Filter line items based on delivery status if toggle is enabled
                  const filteredFields = showOnlyDeliveryItems
                    ? fields.filter((field) => {
                        // Get line item from currentCampaignOrder data
                        const lineItem = currentCampaignOrder.campaigns[campaignIndex]?.lineItems?.[field.name];
                        return lineItem && lineItem.markedForDelivery === true;
                      })
                    : fields;

                  return (
                    <>
                      {filteredFields.map(
                        ({ key, name: fieldNameLineItems, fieldKey, ...restField }) => (
                          <>
                            <CampaignOrderQueueReviewCampaign
                              key={key}
                              form={form}
                              restField={restField}
                              fieldName={fieldNameLineItems}
                              fieldKey={fieldKey}
                              lineItemIndex={fieldNameLineItems}
                              campaignIndex={campaignIndex}
                              onMediaStatusUpdate={onMediaStatusUpdate}
                              isLaunching={isLaunching}
                              isAdvancedMode={isAdvancedMode}
                            />
                            <Divider />
                          </>
                        )
                      )}
                    </>
                  );
                }}
              </Form.List>

              <>
                {/* Date Validation Errors */}
                {dateValidationErrors && dateValidationErrors.length > 0 && (
                  <Alert
                    message="Cannot Launch: Invalid Dates Detected"
                    description={
                      <Space direction="vertical" size="small" style={{ width: '100%' }}>
                        <Text>
                          The following date issues must be fixed in <Text strong>Campaign Requester</Text> before launching:
                        </Text>
                        <ul style={{ marginTop: 8, marginBottom: 8 }}>
                          {dateValidationErrors.map((error, index) => (
                            <li key={index}>
                              <Text strong>{error.entityName}</Text>: {error.message}
                            </li>
                          ))}
                        </ul>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          Update dates in Campaign Requester → {dateValidationErrors.some(e => e.level === 'CAMPAIGN_ORDER') ? 'Campaign Order Details' : dateValidationErrors.some(e => e.level === 'CAMPAIGN') ? 'Campaign Details' : 'Line Items'}
                        </Text>
                        <Button
                          type="link"
                          icon={<InfoCircleOutlined />}
                          onClick={() => {
                            const campaignOrderId = currentCampaignOrder.id;
                            history.push(`/campaigns/requester/edit/${campaignOrderId}`);
                          }}
                          style={{ padding: 0, marginTop: 4 }}
                        >
                          Open Campaign Requester
                        </Button>
                        <Button
                          type="text"
                          size="small"
                          onClick={() => setDateValidationErrors([])}
                          style={{ marginTop: 8 }}
                        >
                          Dismiss
                        </Button>
                      </Space>
                    }
                    type="error"
                    showIcon
                    closable
                    onClose={() => setDateValidationErrors([])}
                    style={{ marginBottom: 16 }}
                  />
                )}

                {(() => {
                  const condition1 = !['LIVE', 'PAUSED'].includes(currentCampaignOrder.campaigns[campaignIndex]?.status);
                  const condition2 = ['LIVE_APPROVED', 'PAUSED_APPROVED', 'PAUSED_PENDING', 'LIVE_PENDING'].includes(currentCampaignOrder.status);
                  const condition3 = !['PENDING', 'LIVE_PENDING'].includes(currentCampaignOrder.status);
                  const condition4 = !isLaunching;
                  // Allow button section if in update mode OR if launch conditions are met
                  const finalCondition = isProgrammaticUpdateMode || ((condition1 || condition2) && condition3 && condition4);

                  return finalCondition;
                })() && (
                  <>
                    {demandSidePlatform === 'NEXXEN' ? (
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'flex-end',
                          gap: 12,
                          width: '100%',
                          justifyContent: 'flex-end',
                        }}
                      >
                        <Button
                          style={{ width: 140 }}
                          type="default"
                          disabled={loadingUpdateCampaignOrder}
                          onClick={() => handleLaunchManual()}
                        >
                          {isCampaignLaunched ? 'Update Manual' : 'Launch Manual'}
                        </Button>
                        {isProgrammaticUpdateMode ? (
                          // UPDATE MODE: Single button, no dropdown
                          <Tooltip
                            title={
                              isAnotherProgrammaticCampaignLaunching
                                ? `Please wait: "${otherProgrammaticLaunchingCampaign?.name || 'Another campaign'}" is currently launching`
                                : ''
                            }
                          >
                            <Button
                              style={{ width: 200 }}
                              type="primary"
                              disabled={
                                loadingUpdateCampaignOrder ||
                                loadingLaunchCampaingOrderProgrammatic ||
                                isAnotherProgrammaticCampaignLaunching
                              }
                              onClick={handleUpdateProgrammatic}
                            >
                              Update Programmatically
                            </Button>
                          </Tooltip>
                        ) : (
                          // LAUNCH MODE: Dropdown with play/pause options
                          <Tooltip
                            title={
                              hasAdProviderId && newLineItemCount === 0 && modifiedLineItemCount === 0
                                ? 'No changes to apply. All line items have already been launched and none have been modified.'
                                : isAnotherProgrammaticCampaignLaunching
                                ? `Please wait: "${otherProgrammaticLaunchingCampaign?.name || 'Another campaign'}" is currently launching`
                                : ''
                            }
                          >
                            <span style={{ display: 'inline-block' }}>
                              <Dropdown
                                trigger={['click']}
                                disabled={!hasNewItemsToLaunch || isAnotherProgrammaticCampaignLaunching || loadingUpdateCampaignOrder || loadingLaunchCampaingOrderProgrammatic}
                                menu={{
                                  items: [
                                    {
                                      key: 'play',
                                      label: isCampaignLaunched ? 'Update Auto Play' : 'Launch Auto Play',
                                      onClick: () => handleLaunchProgrammatic('play')
                                    },
                                    {
                                      key: 'pause',
                                      label: isCampaignLaunched ? 'Update Pause' : 'Launch Pause',
                                      onClick: () => handleLaunchProgrammatic('pause')
                                    }
                                  ]
                                }}
                              >
                                <Button
                                  style={{ width: 200 }}
                                  type="primary"
                                  disabled={
                                    !hasNewItemsToLaunch || loadingUpdateCampaignOrder || loadingLaunchCampaingOrderProgrammatic || isAnotherProgrammaticCampaignLaunching
                                  }
                                >
                                  {isCampaignLaunched ? 'Update Programmatically' : 'Launch Programmatically'}
                                </Button>
                              </Dropdown>
                            </span>
                          </Tooltip>
                        )}
                      </div>
                    ) : (
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'flex-end',
                        }}
                      >
                        <Button
                          style={{ width: 100 }}
                          type="primary"
                          disabled={loadingUpdateCampaignOrder}
                          onClick={() => handleLaunchManual()}
                        >
                          {isCampaignLaunched ? 'Update' : 'Launch'}
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </>
            </Space>
          </Card>
          </Panel>
        </Collapse>
      </div>

      <CampaignLaunchProgressModal
        visible={showProgressModal}
        campaignOrderId={currentCampaignOrder?.id}
        campaignName={currentCampaignOrder?.name || 'Campaign'}
        isUpdateMode={isUpdateMode}
        newLineItemCount={newLineItemCount}
        onCancel={() => setShowProgressModal(false)}
        onCompleted={() => {
          setShowProgressModal(false);
          if (isUpdateMode) {
            message.success(`Successfully added ${newLineItemCount} new line item${newLineItemCount !== 1 ? 's' : ''} to campaign!`);
          } else {
            message.success('Campaign launched successfully!');
          }
          // Navigate based on the launch status
          if (currentLaunchStatus === 'pause') {
            history.push('/campaigns/v2/orderqueue/paused');
          } else {
            history.push('/campaigns/v2/orderqueue/launched');
          }
        }}
        onFailed={(progressData) => {
          setShowProgressModal(false);
          const errorMsg = progressData?.metadata?.errorDetails ||
            (isUpdateMode ? 'Failed to add line items' : 'Campaign launch failed');
          message.error(errorMsg);
          // Failed campaigns might remain in launching tab with error status
        }}
        onError={(error) => {
          console.error('Progress subscription error:', error);
          // Keep modal open but show connection error within modal
        }}
      />

      {/* Add/Update Line Items Confirmation Modal */}
      <Modal
        title={
          newLineItemCount > 0 && modifiedLineItemCount > 0
            ? 'Confirm Adding & Updating Line Items'
            : newLineItemCount > 0
            ? 'Confirm Adding Line Items'
            : 'Confirm Updating Line Items'
        }
        open={showAddLineItemsModal}
        onOk={handleConfirmAddLineItems}
        onCancel={() => setShowAddLineItemsModal(false)}
        okText="Confirm & Launch"
        okButtonProps={{ type: 'primary' }}
        cancelText="Cancel"
        width={600}
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Alert
            message="Update Programmatically"
            description={
              newLineItemCount > 0 && modifiedLineItemCount > 0
                ? `This campaign already has an Ad Provider ID. You are about to add ${newLineItemCount} new line item${newLineItemCount !== 1 ? 's' : ''} and update ${modifiedLineItemCount} existing line item${modifiedLineItemCount !== 1 ? 's' : ''} programmatically.`
                : newLineItemCount > 0
                ? `This campaign already has an Ad Provider ID. You are about to add ${newLineItemCount} new line item${newLineItemCount !== 1 ? 's' : ''} programmatically to the existing campaign.`
                : `You are about to update ${modifiedLineItemCount} existing line item${modifiedLineItemCount !== 1 ? 's' : ''} programmatically.`
            }
            type="info"
            showIcon
          />

          {newLineItemCount > 0 && (
            <div>
              <Text strong>New Line Items to be Added:</Text>
              <ul style={{ marginTop: 8, marginBottom: 0 }}>
                {newLineItems.map((lineItem, index) => (
                  <li key={lineItem.id || index}>
                    <Text>{lineItem.name || `Line Item ${index + 1}`}</Text>
                    {lineItem.budgetTotal && (
                      <Text type="secondary"> - ${lineItem.budgetTotal}</Text>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {modifiedLineItemCount > 0 && (
            <div>
              <Text strong>Existing Line Items to be Updated:</Text>
              <ul style={{ marginTop: 8, marginBottom: 0 }}>
                {modifiedLineItems.map((lineItem, index) => (
                  <li key={lineItem.id || index}>
                    <Text>{lineItem.name || `Line Item ${index + 1}`}</Text>
                    {lineItem.budgetTotal && (
                      <Text type="secondary"> - ${lineItem.budgetTotal}</Text>
                    )}
                    <Text type="secondary" style={{ marginLeft: 8 }}>
                      ({lineItem.status === 'PLAY_PENDING' ? 'Will play' : 'Will pause'})
                    </Text>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {newLineItemCount > 0 && (
            <Alert
              message="Launch Status"
              description={`New line items will be launched with status: ${pendingLaunchStatus === 'play' ? 'LIVE' : 'PAUSED'}`}
              type="warning"
              showIcon
            />
          )}
        </Space>
      </Modal>
    </Spin>
  );
};
